import React, { useMemo } from 'react';
import clsx from 'clsx';
import { Button } from '../button';
import { AsignadosBubbles } from '../AsignadoBubble';
import { tipoBadgeClass } from './OtTarjeta';
import {
  otsPorAvanzar, otsSinResponsable, otsPorAprobar, folioTexto, fasesDe, equipoDe, normalizarAsignados, diasEntre,
} from '../../utils/ordenesTrabajo';
import { InboxIcon, ShieldCheckIcon, CheckIcon, UserMinusIcon } from '@heroicons/react/24/outline';

/**
 * The coordinador's bandeja: every open OT whose fase was declared done,
 * waiting for the relevo. One row, one action.
 *
 * @param {Object} props
 * @param {Array<Object>} props.abiertas  - decorated open OTs
 * @param {Object} props.config  - resolved
 * @param {Array<Object>} props.miembros
 * @param {Function} props.onPasar  - (ot, faseDestinoId|null); same fase = asignar
 * @param {Function} props.onAprobar  - (ot, faseDestinoId|null)
 * @param {Function} props.onRechazar  - (ot)
 * @param {Function} props.onOpen  - (ot)
 * @param {boolean} [props.puedeAprobar]
 * @param {boolean} [props.puedeAsignar]
 */
export function OtBandeja({ abiertas, config, miembros, onPasar, onAprobar, onRechazar, onOpen, puedeAprobar = true, puedeAsignar = true }) {
  const porAvanzar = useMemo(() => (puedeAsignar ? otsPorAvanzar(abiertas) : []), [abiertas, puedeAsignar]);
  const sinResponsable = useMemo(() => (puedeAsignar ? otsSinResponsable(abiertas, config) : []), [abiertas, config, puedeAsignar]);
  const porAprobar = useMemo(() => (puedeAprobar ? otsPorAprobar(abiertas, config) : []), [abiertas, config, puedeAprobar]);
  const now = Date.now();

  if (porAvanzar.length === 0 && sinResponsable.length === 0 && porAprobar.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-16 text-zinc-400 bg-white rounded-lg border border-zinc-200">
        <InboxIcon className="w-10 h-10" />
        <span className="text-sm text-zinc-500">Nada pendiente de ti.</span>
      </div>
    );
  }

  const encabezado = (ot) => {
    return (
      <td className="px-3 py-2.5 cursor-pointer" onClick={() => onOpen?.(ot)}>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold tabular-nums text-azul-surelia">{folioTexto(ot.folio)}</span>
          <span className={clsx('rounded-full px-2 py-0.5 text-[10px] font-medium', tipoBadgeClass(ot.tipo))}>{ot.tipoNombre || ot.tipo}</span>
        </div>
        <div className="font-medium text-zinc-800">{ot.titulo}</div>
      </td>
    );
  };

  return (
    <div className="space-y-4">
      {porAprobar.length > 0 && (
        <div className="bg-white rounded-lg border border-amber-200 shadow-sm overflow-x-auto">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-amber-100 text-sm font-semibold text-amber-800">
            <ShieldCheckIcon className="w-4 h-4" />Por aprobar
            <span className="rounded-full bg-amber-100 px-1.5 text-[11px] tabular-nums">{porAprobar.length}</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-azul-surelia text-left text-xs font-medium text-white">
                <th className="px-3 py-2">Orden</th>
                <th className="px-3 py-2">Fase</th>
                <th className="px-3 py-2">Llegó de</th>
                <th className="px-3 py-2 text-right">Espera</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {porAprobar.map((ot) => {
                const fases = fasesDe(config, ot.tipo);
                const idx = fases.findIndex((f) => f.id === ot.faseId);
                const sig = idx >= 0 ? fases[idx + 1] : null;
                const previa = [...(ot.historialFases || [])].reverse().find((h) => h.hasta != null);
                const faseP = previa ? fases.find((f) => f.id === previa.faseId) : null;
                const espera = diasEntre(ot.faseDesde || 0, now);
                return (
                  <tr key={ot.id} className="hover:bg-zinc-50">
                    {encabezado(ot)}
                    <td className="px-3 py-2.5 text-zinc-700">{ot.faseNombre || '—'}</td>
                    <td className="px-3 py-2.5">
                      {faseP ? <div className="text-zinc-700">{faseP.nombre}</div> : <span className="text-zinc-400">—</span>}
                      <AsignadosBubbles uids={previa?.asignados || []} miembros={miembros} size="sm" max={3} />
                    </td>
                    <td className={clsx('px-3 py-2.5 text-right tabular-nums', espera >= 2 ? 'text-red-600 font-medium' : espera >= 1 ? 'text-amber-600' : 'text-zinc-600')}>{espera} d</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-2">
                        <Button outline color="surelia" onClick={() => onRechazar(ot)}>Rechazar</Button>
                        <Button color="surelia" onClick={() => onAprobar(ot, sig?.id || null)}>Aprobar</Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {porAvanzar.length > 0 && (
        <div className="bg-white rounded-lg border border-emerald-200 shadow-sm overflow-x-auto">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-emerald-100 text-sm font-semibold text-emerald-800">
            <CheckIcon className="w-4 h-4" />Terminaron su fase
            <span className="rounded-full bg-emerald-100 px-1.5 text-[11px] tabular-nums">{porAvanzar.length}</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-azul-surelia text-left text-xs font-medium text-white">
                <th className="px-3 py-2 w-full">Orden</th>
                <th className="px-3 py-2 whitespace-nowrap">Fase terminada</th>
                <th className="px-3 py-2 whitespace-nowrap">Trabajaron</th>
                <th className="px-3 py-2 whitespace-nowrap">Sigue</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Espera</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {porAvanzar.map((ot) => {
                const fases = fasesDe(config, ot.tipo);
                const idx = fases.findIndex((f) => f.id === ot.faseId);
                const sig = idx >= 0 ? fases[idx + 1] : null;
                const equipoSig = sig?.equipoId ? equipoDe(config.equipos, sig.equipoId) : null;
                const espera = diasEntre(ot.faseTerminada.at, now);
                return (
                  <tr key={ot.id} className="hover:bg-zinc-50">
                    {encabezado(ot)}
                    <td className="px-3 py-2.5 text-zinc-700 whitespace-nowrap">
                      <div>{ot.faseNombre || '—'}</div>
                      <div className="text-xs text-zinc-500">
                        {ot.faseTerminada.nombre ? `por ${ot.faseTerminada.nombre}` : ''}
                        {ot.faseTerminada.nota ? ` · ${ot.faseTerminada.nota}` : ''}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <AsignadosBubbles uids={normalizarAsignados(ot)} miembros={miembros} size="sm" max={3} />
                    </td>
                    <td className="px-3 py-2.5 text-zinc-700 whitespace-nowrap">
                      {sig ? (
                        <>
                          <div>{sig.nombre}</div>
                          {equipoSig && <div className="text-xs text-zinc-500">{equipoSig.nombre}</div>}
                        </>
                      ) : (
                        <span className="text-zinc-400">Última fase</span>
                      )}
                    </td>
                    <td className={clsx('px-3 py-2.5 text-right tabular-nums whitespace-nowrap', espera >= 2 ? 'text-red-600 font-medium' : espera >= 1 ? 'text-amber-600' : 'text-zinc-600')}>
                      {espera} d
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <Button color="surelia" onClick={() => onPasar(ot, sig?.id || null)}>
                        {sig ? `Avanzar a ${sig.nombre}` : 'Mover'}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {sinResponsable.length > 0 && (
        <div className="bg-white rounded-lg border border-zinc-200 shadow-sm overflow-x-auto">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100 text-sm font-semibold text-zinc-700">
            <UserMinusIcon className="w-4 h-4" />Sin responsable
            <span className="rounded-full bg-zinc-100 px-1.5 text-[11px] tabular-nums">{sinResponsable.length}</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-azul-surelia text-left text-xs font-medium text-white">
                <th className="px-3 py-2 w-full">Orden</th>
                <th className="px-3 py-2 whitespace-nowrap">Fase</th>
                <th className="px-3 py-2 whitespace-nowrap">Equipo</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Lleva</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {sinResponsable.map((ot) => {
                const fases = fasesDe(config, ot.tipo);
                const fase = fases.find((f) => f.id === ot.faseId) || null;
                const equipo = fase?.equipoId ? equipoDe(config.equipos, fase.equipoId) : null;
                const lleva = diasEntre(ot.faseDesde || now, now);
                return (
                  <tr key={ot.id} className="hover:bg-zinc-50">
                    {encabezado(ot)}
                    <td className="px-3 py-2.5 text-zinc-700 whitespace-nowrap">{fase?.nombre || ot.faseNombre || '—'}</td>
                    <td className="px-3 py-2.5 text-zinc-600 whitespace-nowrap">{equipo?.nombre || <span className="text-zinc-400">Sin equipo</span>}</td>
                    <td className={clsx('px-3 py-2.5 text-right tabular-nums whitespace-nowrap', lleva >= 2 ? 'text-red-600 font-medium' : lleva >= 1 ? 'text-amber-600' : 'text-zinc-600')}>
                      {lleva} d
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <Button color="surelia" onClick={() => onPasar(ot, ot.faseId)}>Asignar</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default OtBandeja;
